import React from 'react';
import { Control, Controller, Path } from 'react-hook-form';
import {
  AccountCreationUiModuleBody,
  AccountLoadingUiModuleBody,
  AiSearchUiModuleBody,
  GeoBlockUiModuleBody,
  LinkingAccountUiModuleBody,
  NewPartnerLandingUiModuleBody,
  NotificationsUiModuleBody,
  OneBrandPromoUiModuleBody,
  OutboundUiModuleBody,
  PlanSummaryUiModuleBody,
  PlaybackPaywallUiModuleBody,
  ProfileUiModuleBody,
  RegistrationWallUiModuleBody,
  SpinPremiaUiModuleBody,
  TdsGiftCardsUiModuleBody,
  WhoIsWatchingUiModuleBody,
  ShortNovelasClipsUiModuleBody,
  PlanPickerBody,
  PlanPickerResponse,
  SubscriptionPlanDetailsUiModuleBody
} from '../../../API';
import { useLocales } from '../../../hooks';
import { LocaleKeys } from '../../../locales/i18n';
import { FormField } from '../../Layouts/UIModuleForm/styles';
import { LokaliseAutocomplete } from '../LokaliseAutocomplete';
import { makeStyles } from 'tss-react/mui';
import { Typography } from '@mui/material';

const useStyles = makeStyles()((theme) => ({
  error: {
    marginBottom: theme.spacing(0),
    '& .MuiFormControl-root': {
      marginBottom: theme.spacing(0)
    },
    '& .MuiInputBase-root::before': {
      borderBottom: `1px solid ${theme.palette.error.main}`
    },
    '& .MuiFormLabel-root': {
      color: theme.palette.error.main
    }
  },
  errorLabel: {
    color: theme.palette.error.main,
    fontSize: theme.typography.fontSize * 0.92
  }
}));

type ControlType =
  | Control<AccountCreationUiModuleBody, unknown>
  | Control<AccountLoadingUiModuleBody, unknown>
  | Control<AiSearchUiModuleBody, unknown>
  | Control<GeoBlockUiModuleBody, unknown>
  | Control<LinkingAccountUiModuleBody, unknown>
  | Control<NewPartnerLandingUiModuleBody, unknown>
  | Control<NotificationsUiModuleBody, unknown>
  | Control<OneBrandPromoUiModuleBody, unknown>
  | Control<OutboundUiModuleBody, unknown>
  | Control<PlanSummaryUiModuleBody, unknown>
  | Control<PlaybackPaywallUiModuleBody, unknown>
  | Control<ProfileUiModuleBody, unknown>
  | Control<RegistrationWallUiModuleBody, unknown>
  | Control<SpinPremiaUiModuleBody, unknown>
  | Control<TdsGiftCardsUiModuleBody, unknown>
  | Control<WhoIsWatchingUiModuleBody, unknown>
  | Control<ShortNovelasClipsUiModuleBody, unknown>
  | Control<PlanPickerBody, unknown>
  | Control<PlanPickerResponse, unknown>
  | Control<SubscriptionPlanDetailsUiModuleBody, unknown>;

type NameType =
  | keyof AccountCreationUiModuleBody
  | keyof AccountLoadingUiModuleBody
  | keyof AiSearchUiModuleBody
  | keyof GeoBlockUiModuleBody
  | keyof LinkingAccountUiModuleBody
  | keyof NewPartnerLandingUiModuleBody
  | keyof NotificationsUiModuleBody
  | Path<OneBrandPromoUiModuleBody>
  | keyof OutboundUiModuleBody
  | keyof PlanSummaryUiModuleBody
  | keyof PlaybackPaywallUiModuleBody
  | Path<ProfileUiModuleBody>
  | keyof RegistrationWallUiModuleBody
  | Path<SpinPremiaUiModuleBody>
  | keyof TdsGiftCardsUiModuleBody
  | keyof WhoIsWatchingUiModuleBody
  | keyof ShortNovelasClipsUiModuleBody
  | Path<PlanPickerBody>
  | Path<PlanPickerResponse>
  | keyof SubscriptionPlanDetailsUiModuleBody;

type LokaliseFieldProps = {
  name: NameType;
  control: ControlType;
  toTranslate?: string;
  label?: string;
  dataTestId: string;
  required?: boolean;
};

export const LokaliseField: React.FC<LokaliseFieldProps> = ({
  name,
  control,
  toTranslate,
  label,
  dataTestId,
  required = false
}) => {
  const { t } = useLocales();
  const { classes } = useStyles();

  return (
    <FormField>
      <Controller
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        control={control as Control<any>}
        name={name as NameType}
        rules={required ? { required: t('general.field_is_required') } : undefined}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <>
            <LokaliseAutocomplete
              label={!label ? t(`${toTranslate}.${name}` as LocaleKeys) : t(label as LocaleKeys)}
              name={name}
              value={value as string}
              onChange={onChange}
              required={required}
              data-testid={dataTestId}
              classNames={error ? classes.error : undefined}
            />
            {!!error && <Typography className={classes.errorLabel}>{error.message}</Typography>}
          </>
        )}
      />
    </FormField>
  );
};
